// Merge several invoices for the same client into one.

import { computeTotals, invoiceStatus, num, uid, sortLines } from "./utils";
import { invoiceTags } from "./tags";

export function mergeProblems(list) {
  if (list.length < 2) return "Select at least two invoices.";
  const [a] = list;
  if (list.some((i) => (i.docType || "invoice") !== (a.docType || "invoice"))) return "All selected documents must be the same type.";
  if (list.some((i) => i.docType === "credit")) return "Credit notes can’t be merged.";
  if (list.some((i) => (i.clientId || i.billTo?.name) !== (a.clientId || a.billTo?.name))) return "All invoices must be for the same client.";
  if (list.some((i) => i.currency !== a.currency)) return "All invoices must be in the same currency.";
  if (list.some((i) => invoiceStatus(i) === "cancelled")) return "Cancelled invoices can’t be merged.";
  if (list.some((i) => computeTotals(i).paid > 0)) return "One of the invoices already has payments or credit notes – remove them first.";
  return "";
}

const shortPeriod = (inv) => inv.period || inv.date;

/**
 * @param list invoices to merge
 * @param opts { keep: "earliest" | "new", newNumber, prefix: "none" | "period" | "number", combine: boolean }
 * @returns { merged, sources } where merged is the invoice to save and sources are the originals it replaces
 */
export function buildMerged(list, opts) {
  const sorted = [...list].sort((a, b) => (a.date || "").localeCompare(b.date || "") || (a.number || "").localeCompare(b.number || ""));
  const base = sorted[0];
  let items = [];
  for (const inv of sorted) {
    for (const l of inv.items || []) {
      const label = opts.prefix === "period" ? `${shortPeriod(inv)} – ` : opts.prefix === "number" ? `[${inv.number}] ` : "";
      items.push({ ...l, id: uid(), description: label + (l.description || "") });
    }
  }
  if (opts.combine && opts.prefix === "none") {
    const map = new Map();
    for (const l of items) {
      const key = [l.description.trim().toLowerCase(), num(l.rate), num(l.taxRate), l.tag || "", l.hsn || ""].join("|");
      if (map.has(key)) map.get(key).qty = num(map.get(key).qty) + num(l.qty);
      else map.set(key, { ...l });
    }
    items = [...map.values()];
  }
  items = sortLines(items); // invoice lines are always A–Z
  const periods = [...new Set(sorted.map((i) => i.period).filter(Boolean))];
  const merged = {
    ...structuredClone(base),
    id: opts.keep === "earliest" ? base.id : undefined,
    number: opts.keep === "earliest" ? base.number : opts.newNumber,
    date: sorted.map((i) => i.date).sort().pop(),
    dueDate: sorted.map((i) => i.dueDate || "").sort().pop(),
    items,
    discount: sorted.reduce((s, i) => s + num(i.discount), 0),
    credit: sorted.reduce((s, i) => s + num(i.credit), 0),
    tags: [...new Set(sorted.flatMap((i) => i.tags || []))],
    period: periods.length > 1 ? `${periods[0]} – ${periods[periods.length - 1]}` : periods[0] || "",
    periodMonth: base.periodMonth || "",
    notes: [...new Set(sorted.map((i) => i.notes).filter(Boolean))].join("\n"),
    status: sorted.some((i) => i.status === "draft") ? "draft" : base.status,
    mergedFrom: sorted.map((i) => i.number),
    payments: [],
  };
  if (opts.keep !== "earliest") delete merged.createdAt;
  const sources = opts.keep === "earliest" ? sorted.slice(1) : sorted;
  return { merged, sources, allTags: invoiceTags(merged) };
}
